import React, { useState, useEffect } from 'react';
import { furnitureApi, locationsApi, reportsApi, exportsApi, furnitureTypesApi } from '../services/api';
import { usePreferences } from '../contexts/PreferencesContext';
import {
  FileBarChart,
  Download,
  Filter,
  FileText,
  Table as TableIcon,
  X,
  FileSpreadsheet,
  Calculator,
  ArrowRightLeft,
  ClipboardCheck,
  Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const STATUS_CONFIG = {
  neuf: { label: 'Neuf', class: 'bg-emerald-50 text-emerald-700' },
  bon: { label: 'Bon', class: 'bg-blue-50 text-blue-700' },
  usage: { label: 'Usagé', class: 'bg-amber-50 text-amber-700' },
  hors_service: { label: 'Hors service', class: 'bg-rose-50 text-rose-700' },
  en_maintenance: { label: 'En maintenance', class: 'bg-orange-50 text-orange-700' }
};

const ReportsPage = () => {
  const { formatCurrency, currency } = usePreferences();
  const [sites, setSites] = useState([]);
  const [furniture, setFurniture] = useState([]);
  const [stats, setStats] = useState({ total: 0, totalValue: 0, byStatus: {}, byType: {} });
  const [loading, setLoading] = useState(false);
  const [filterSite, setFilterSite] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Furniture types (dynamic from API)
  const [furnitureTypes, setFurnitureTypes] = useState([]);
  const [typeLabels, setTypeLabels] = useState({});

  // Export comptable states
  const [exportSite, setExportSite] = useState('');
  const [exportDateDebut, setExportDateDebut] = useState('');
  const [exportDateFin, setExportDateFin] = useState('');
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [exportTypeMouvement, setExportTypeMouvement] = useState('');

  // Chargements uniques au montage
  useEffect(() => {
    loadSites();
    loadFurnitureTypes();
  }, []);

  // Rechargement uniquement quand les filtres changent
  useEffect(() => {
    loadReport();
  }, [filterSite, filterType, filterStatus]);

  const loadFurnitureTypes = async () => {
    try {
      const response = await furnitureTypesApi.getAll();
      setFurnitureTypes(response.data);
      const labels = response.data.reduce((acc, ft) => {
        acc[ft.code] = ft.label;
        return acc;
      }, {});
      setTypeLabels(labels);
    } catch (error) {
      console.error('Erreur chargement types mobilier:', error);
    }
  };

  const loadSites = async () => {
    try {
      const response = await locationsApi.getSites();
      setSites(response.data);
    } catch (error) {
      console.error('Erreur chargement sites:', error);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterSite) params.site_id = filterSite;
      if (filterType) params.type_mobilier = filterType;
      if (filterStatus) params.etat = filterStatus;

      // Agrégats via un seul pipeline MongoDB (rapide)
      // + seulement 50 items pour l'affichage du tableau
      const [statsRes, furnitureRes] = await Promise.all([
        reportsApi.getStats(params),
        furnitureApi.getAll({ ...params, limit: 50 }),
      ]);
      setStats(statsRes.data);
      setFurniture(furnitureRes.data);
    } catch (error) {
      console.error('Erreur chargement rapport:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Export comptable handlers
  const downloadExport = async (type) => {
    const token = localStorage.getItem('token');
    const params = { format: exportFormat, currency: currency };
    if (exportSite) params.site_id = exportSite;
    if (exportDateDebut) params.date_debut = exportDateDebut;
    if (exportDateFin) params.date_fin = exportDateFin;
    if (type === 'mouvements' && exportTypeMouvement) {
      params.type_mouvement = exportTypeMouvement;
    }
    
    let url;
    switch (type) {
      case 'immobilisations':
        url = exportsApi.getImmobilisationsUrl(params);
        break;
      case 'mouvements':
        url = exportsApi.getMouvementsUrl(params);
        break;
      case 'inventaires':
        url = exportsApi.getInventairesUrl(params);
        break;
      default:
        return;
    }
    
    try {
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${type}_${new Date().toISOString().split('T')[0]}.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error(`Erreur export ${type}:`, error);
    }
  };

  const exportCSV = async () => {
    try {
      const params = { format: 'csv' };
      if (filterSite) params.site_id = filterSite;
      if (filterType) params.type_mobilier = filterType;
      if (filterStatus) params.etat = filterStatus;

      const response = await reportsApi.export(params);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mobilier_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur export CSV:', error);
    }
  };

  const exportJSON = async () => {
    try {
      const params = { format: 'json' };
      if (filterSite) params.site_id = filterSite;
      if (filterType) params.type_mobilier = filterType;
      if (filterStatus) params.etat = filterStatus;

      const response = await reportsApi.export(params);
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mobilier_export_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur export JSON:', error);
    }
  };

  const clearFilters = () => {
    setFilterSite('');
    setFilterType('');
    setFilterStatus('');
  };

  const hasFilters = filterSite || filterType || filterStatus;

  // Stats issues du backend (agrégation MongoDB)
  const { total, totalValue, byStatus, byType } = stats;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            Rapports & Exports
          </h1>
          <p className="text-slate-500 mt-1">Générez des rapports et exportez vos données comptables</p>
        </div>
      </div>

      <Tabs defaultValue="rapports" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="rapports" className="flex items-center gap-2">
            <FileBarChart className="w-4 h-4" />
            Rapports
          </TabsTrigger>
          <TabsTrigger value="exports" className="flex items-center gap-2" data-testid="exports-comptables-tab">
            <Calculator className="w-4 h-4" />
            Exports Comptables
          </TabsTrigger>
        </TabsList>

        {/* Onglet Rapports */}
        <TabsContent value="rapports" className="space-y-6 mt-6">
          {/* Export buttons */}
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={exportJSON}
              data-testid="export-json-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Export JSON
            </Button>
            <Button
              onClick={exportCSV}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>

          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Filter className="w-5 h-5 text-slate-400" />
                Filtres
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 items-end">
                <div className="space-y-2">
                  <Label>Site</Label>
                  <Select value={filterSite || "all"} onValueChange={(v) => setFilterSite(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[180px]" data-testid="filter-site">
                      <SelectValue placeholder="Tous les sites" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les sites</SelectItem>
                      {sites.map(site => (
                        <SelectItem key={site.id} value={site.id}>{site.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]" data-testid="filter-type">
                  <SelectValue placeholder="Tous les types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {furnitureTypes.map(ft => (
                    <SelectItem key={ft.code} value={ft.code}>{ft.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>État</Label>
              <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]" data-testid="filter-status">
                  <SelectValue placeholder="Tous les états" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les états</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {hasFilters && (
              <Button variant="ghost" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Réinitialiser
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500">Total mobilier</p>
            <p className="text-3xl font-heading font-bold mt-2">{total}</p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500">Valeur totale</p>
            <p className="text-3xl font-heading font-bold mt-2">
              {formatCurrency(totalValue)}
            </p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500">En bon état</p>
            <p className="text-3xl font-heading font-bold mt-2 text-emerald-600">
              {(byStatus.neuf || 0) + (byStatus.bon || 0)}
            </p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500">À renouveler</p>
            <p className="text-3xl font-heading font-bold mt-2 text-rose-600">
              {(byStatus.hors_service || 0) + (byStatus.usage || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Report Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <TableIcon className="w-5 h-5 text-slate-400" />
            Détail du rapport ({total} éléments)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : furniture.length === 0 ? (
            <div className="text-center py-16">
              <FileBarChart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">Aucune donnée</h3>
              <p className="text-slate-500 mt-1">Ajustez vos filtres ou ajoutez du mobilier</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th>Code-barres</th>
                    <th>Type</th>
                    <th>Marque / Modèle</th>
                    <th>Site</th>
                    <th>État</th>
                    <th className="text-right">Valeur</th>
                  </tr>
                </thead>
                <tbody>
                  {furniture.map((item) => (
                    <tr key={item.id} className="table-row-hover">
                      <td className="barcode-text">{item.code_barres}</td>
                      <td>{typeLabels[item.type_mobilier] || item.type_mobilier}</td>
                      <td>
                        <div>
                          <span className="font-medium">{item.marque || '-'}</span>
                          {item.modele && <span className="text-slate-500 ml-1">/ {item.modele}</span>}
                        </div>
                      </td>
                      <td>{item.site_nom || '-'}</td>
                      <td>
                        <Badge className={STATUS_CONFIG[item.etat]?.class}>
                          {STATUS_CONFIG[item.etat]?.label}
                        </Badge>
                      </td>
                      <td className="text-right font-mono">
                        {item.cout_achat ? formatCurrency(item.cout_achat) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {total > 50 && (
                <div className="text-center py-4 border-t text-slate-500 text-sm">
                  Affichage des 50 premiers éléments sur {total}. Exportez pour voir la liste complète.
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Breakdown by Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Répartition par état</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                const count = byStatus[key] || 0;
                const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                
                return (
                  <div key={key} className="flex items-center gap-3">
                    <Badge className={config.class}>{config.label}</Badge>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-slate-400 rounded-full"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-sm font-mono w-16 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Répartition par type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {furnitureTypes.map(ft => {
                const count = byType[ft.code] || 0;
                const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                
                if (count === 0) return null;
                
                return (
                  <div key={ft.code} className="flex items-center gap-3">
                    <span className="text-sm w-28 text-slate-600">{ft.label}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-sm font-mono w-16 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
        </TabsContent>

        {/* Onglet Exports Comptables */}
        <TabsContent value="exports" className="space-y-6 mt-6">
          {/* Filtres globaux pour exports */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Filter className="w-5 h-5 text-slate-400" />
                Filtres d'export
              </CardTitle>
              <CardDescription>
                Ces filtres s'appliquent à tous les exports comptables
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 items-end">
                <div className="space-y-2">
                  <Label>Site</Label>
                  <Select value={exportSite || "all"} onValueChange={(val) => setExportSite(val === "all" ? "" : val)}>
                    <SelectTrigger className="w-[200px]" data-testid="export-filter-site">
                      <SelectValue placeholder="Tous les sites" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les sites</SelectItem>
                      {sites.map(site => (
                        <SelectItem key={site.id} value={site.id}>{site.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Date début</Label>
                  <Input
                    type="date"
                    value={exportDateDebut}
                    onChange={(e) => setExportDateDebut(e.target.value)}
                    className="w-[160px]"
                    data-testid="export-date-debut"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Date fin</Label>
                  <Input
                    type="date"
                    value={exportDateFin}
                    onChange={(e) => setExportDateFin(e.target.value)}
                    className="w-[160px]"
                    data-testid="export-date-fin"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Format</Label>
                  <Select value={exportFormat} onValueChange={setExportFormat}>
                    <SelectTrigger className="w-[140px]" data-testid="export-format">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                      <SelectItem value="csv">CSV (.csv)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cards d'export */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Export Immobilisations */}
            <Card className="border-2 hover:border-blue-300 transition-colors">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Calculator className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="font-heading text-lg">Immobilisations</CardTitle>
                    <CardDescription>Registre des actifs avec amortissements</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm text-slate-600 space-y-1">
                  <p>• Code-barres et désignation</p>
                  <p>• Date et coût d'acquisition</p>
                  <p>• Durée d'amortissement</p>
                  <p>• Valeur nette comptable</p>
                  <p>• Localisation et état</p>
                </div>
                <Button 
                  onClick={() => downloadExport('immobilisations')}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="export-immobilisations-btn"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Télécharger
                </Button>
              </CardContent>
            </Card>

            {/* Export Mouvements */}
            <Card className="border-2 hover:border-green-300 transition-colors">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <ArrowRightLeft className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="font-heading text-lg">Mouvements</CardTitle>
                    <CardDescription>Entrées, sorties et transferts</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm">Type de mouvement</Label>
                  <Select value={exportTypeMouvement || "all"} onValueChange={(val) => setExportTypeMouvement(val === "all" ? "" : val)}>
                    <SelectTrigger className="w-full" data-testid="export-type-mouvement">
                      <SelectValue placeholder="Tous les types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les types</SelectItem>
                      <SelectItem value="entree">Entrée</SelectItem>
                      <SelectItem value="sortie">Sortie</SelectItem>
                      <SelectItem value="transfert">Transfert</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="rebut">Mise au rebut</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-slate-600 space-y-1">
                  <p>• Date et type de mouvement</p>
                  <p>• Désignation du mobilier</p>
                  <p>• Valeur et commentaire</p>
                </div>
                <Button 
                  onClick={() => downloadExport('mouvements')}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="export-mouvements-btn"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Télécharger
                </Button>
              </CardContent>
            </Card>

            {/* Export Inventaires */}
            <Card className="border-2 hover:border-orange-300 transition-colors">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <ClipboardCheck className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <CardTitle className="font-heading text-lg">Inventaires</CardTitle>
                    <CardDescription>Résultats et valorisation des écarts</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm text-slate-600 space-y-1">
                  <p>• Synthèse par inventaire</p>
                  <p>• Mobiliers trouvés/manquants</p>
                  <p>• Valorisation des écarts</p>
                  <p>• Taux de conformité</p>
                </div>
                <Button 
                  onClick={() => downloadExport('inventaires')}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  data-testid="export-inventaires-btn"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Télécharger
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Info sur la compatibilité */}
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <FileSpreadsheet className="w-5 h-5 text-slate-500 mt-0.5" />
                <div className="text-sm text-slate-600">
                  <p className="font-medium text-slate-700 mb-1">Compatibilité logiciels comptables</p>
                  <p>Les fichiers exportés sont compatibles avec la plupart des logiciels de comptabilité (Sage, Cegid, EBP, QuickBooks, Odoo...) et peuvent être importés directement dans Excel ou LibreOffice Calc.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
